/**
 * SalesByRep — R5 wave 2 (2026-05-17).
 *
 * Per R5-D6 (audit-amended): "Sales by rep" = credited rep on CTAs
 * (event_call_to_actions.sales_position_id OR organization_ctas.sales_position_id).
 * Includes "Unattributed" bucket for CTAs with NULL credited rep
 * (pre-R2B CTAs predate per-rep attribution). Do NOT silently fall back
 * to the event's lead/event rep — that's misleading attribution per audit.
 *
 * Rep names are hydrated client-side via resolve_position_names_bulk for
 * display; the underlying aggregation RPC returns only position UUIDs +
 * counts to keep server work minimal.
 */

import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getSalesByRep,
  resolvePositionNamesBulk,
  indexPositionsById,
  type AggregationFilter,
  type SalesByRepRow,
} from "../../services/reportsSuite.service";
import ExportButton from "../../components/common/ExportButton";
import ReportFiltersBar, {
  type ReportFilterState,
} from "../../components/reports/ReportFiltersBar";
import ReportPageHeader from "../../components/reports/ReportPageHeader";
import VMCutoverBanner from "../../components/reports/VMCutoverBanner";

const PAGE_SIZE = 50;

const filterToRpc = (
  f: ReportFilterState,
  page: number,
  limit: number,
  includeUnattributed: boolean,
): AggregationFilter & { includeUnattributed: boolean } => ({
  orgIds: f.orgId ? [f.orgId] : undefined,
  state: f.state,
  dateFrom: f.dateFrom,
  dateTo: f.dateTo,
  page,
  limit,
  includeUnattributed,
});

const SalesByRep: React.FC = () => {
  const [filters, setFilters] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);
  const [includeUnattributed, setIncludeUnattributed] = useState(true);

  React.useEffect(() => {
    setPage(1);
  }, [
    filters.dateFrom,
    filters.dateTo,
    filters.state,
    filters.orgId,
    includeUnattributed,
  ]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["report-sales-by-rep", filters, page, includeUnattributed],
    queryFn: () =>
      getSalesByRep(filterToRpc(filters, page, PAGE_SIZE, includeUnattributed)),
    keepPreviousData: true,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const positionIds = useMemo(
    () =>
      Array.from(
        new Set(
          rows
            .map((r: any) => r.credited_rep_position_id)
            .filter((id: any): id is string => Boolean(id)),
        ),
      ),
    [rows],
  );

  const { data: positionRows } = useQuery({
    queryKey: ["sales-by-rep-positions", positionIds],
    queryFn: () => resolvePositionNamesBulk(positionIds),
    enabled: positionIds.length > 0,
  });

  const positionsById = useMemo(
    () => indexPositionsById(positionRows ?? []),
    [positionRows],
  );

  const repDisplay = (positionId: string | null) => {
    if (!positionId) {
      return "Unattributed";
    }
    const p = positionsById.get(positionId);
    if (!p) {
      return positionId;
    }
    const label = p.assigned_name || p.title;
    return p.is_active ? label : `${label} (inactive)`;
  };

  const EXPORT_COLUMNS = [
    "Credited Rep",
    "Position ID",
    "Voucher Count",
    "Verified Members",
    "Events Credited On",
  ];

  // Post-ship audit must-fix #2: bulk-hydrate rep names against
  // pageRows, not visible-page cache. Without this, exports beyond
  // page 1 emit position IDs instead of names.
  const chunk = <T,>(arr: T[], size: number): T[][] => {
    const out: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      out.push(arr.slice(i, i + size));
    }
    return out;
  };

  const mapRows = async (
    pageRows: SalesByRepRow[],
  ): Promise<Record<string, unknown>[]> => {
    const ids = Array.from(
      new Set(
        pageRows
          .map((r) => r.credited_rep_position_id)
          .filter((id): id is string => Boolean(id)),
      ),
    );
    const chunks = await Promise.all(
      chunk(ids, 5000).map((c) => resolvePositionNamesBulk(c)),
    );
    const exportPositionsById = indexPositionsById(chunks.flat());
    const exportRepDisplay = (positionId: string | null) => {
      if (!positionId) {
        return "Unattributed";
      }
      const p = exportPositionsById.get(positionId);
      if (!p) {
        return positionId;
      }
      const label = p.assigned_name || p.title;
      return p.is_active ? label : `${label} (inactive)`;
    };
    return pageRows.map((r) => ({
      "Credited Rep": exportRepDisplay(r.credited_rep_position_id),
      "Position ID": r.credited_rep_position_id || "",
      "Voucher Count": r.voucher_count,
      "Verified Members": r.verified_member_count,
      "Events Credited On": r.event_count,
    }));
  };

  return (
    <div className="space-y-4">
      <VMCutoverBanner />
      <ReportPageHeader
        title="Sales by Rep"
        description={
          <>
            Vouchers grouped by the credited rep on each CTA. "Unattributed"
            captures CTAs without a credited rep (typical for pre-R2B generic
            event QR codes).
          </>
        }
        actions={
          <ExportButton
            filters={filters}
            fetchPage={(f, p, l) =>
              getSalesByRep(filterToRpc(f, p, l, includeUnattributed)).then(
                (res) => ({ rows: res.rows, total: res.total }),
              )
            }
            mapRows={mapRows}
            columns={EXPORT_COLUMNS}
            filename="sales-by-rep"
          />
        }
      />

      <ReportFiltersBar
        filters={filters}
        onChange={setFilters}
        showType={false}
        showStatus={false}
      />

      <label className="inline-flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={includeUnattributed}
          onChange={(e) => setIncludeUnattributed(e.target.checked)}
          className="rounded border-gray-300"
        />
        Include Unattributed bucket
      </label>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">Credited Rep</th>
              <th
                className="px-3 py-2 text-right"
                title="New (attendee-based): distinct contacts with a QR-scan signup"
              >
                Contacts (new)
              </th>
              <th
                className="px-3 py-2 text-right"
                title="New: contacts above with a Zuora-confirmed subscription"
              >
                Verified (Zuora)
              </th>
              <th
                className="px-3 py-2 text-right"
                title="Awaiting Zuora webhook confirmation"
              >
                Pending Zuora
              </th>
              <th
                className="px-3 py-2 text-right"
                title="Distinct events the rep had attendees on"
              >
                Events (new)
              </th>
              <th
                className="px-3 py-2 text-right text-gray-400"
                title="Legacy voucher-based count (cutover window)"
              >
                Vouchers (legacy)
              </th>
              <th
                className="px-3 py-2 text-right text-gray-400"
                title="Legacy voucher-based verified contacts (cutover window)"
              >
                Verified (legacy)
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
            {error && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-red-600">
                  {(error as Error).message}
                </td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                  No signups match the current filters.
                </td>
              </tr>
            )}
            {rows.map((r: any, i: any) => (
              <tr
                key={r.credited_rep_position_id || `unattributed-${i}`}
                className={`hover:bg-gray-50 ${
                  !r.credited_rep_position_id ? "bg-yellow-50" : ""
                }`}
              >
                <td className="px-3 py-2 font-medium">
                  {repDisplay(r.credited_rep_position_id)}
                </td>
                <td className="px-3 py-2 text-right">
                  {(r.attributed_contact_count ?? 0).toLocaleString()}
                </td>
                <td className="px-3 py-2 text-right">
                  {(r.verified_member_count_new ?? 0).toLocaleString()}
                </td>
                <td className="px-3 py-2 text-right">
                  {(r.pending_zuora_count ?? 0).toLocaleString()}
                </td>
                <td className="px-3 py-2 text-right">
                  {(r.attendee_event_count ?? 0).toLocaleString()}
                </td>
                <td className="px-3 py-2 text-right text-gray-400">
                  {r.voucher_count.toLocaleString()}
                </td>
                <td className="px-3 py-2 text-right text-gray-400">
                  {r.verified_member_count.toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-600">
        <div>
          {total > 0
            ? `Page ${page} of ${totalPages} — ${total.toLocaleString()} reps`
            : ""}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default SalesByRep;
